import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { space, controlHeight, size, transition } from '../../constants.js';
import { validFocusStyle, invalidFocusStyle } from '../../styles/focusStyles.js';
import { Spinner } from '../Spinner/Spinner.js';
import { palette } from '../../palette/index.js';
import { Box } from '../Box/Box.js';
import { fontWeight } from '../../styles/fontStyles.js';

const filterProps = (prop)=>prop !== "scale" && isPropValid(prop);
const defaultScale = "medium";
const defaultMeaning = "neutral";
const defaultProminence = "medium";
const contentDimensions = {
    small: {
        gap: space.s1 * 3,
        paddingInline: space.s1 * 1.5
    },
    medium: {
        gap: space.s2 * 2,
        paddingInline: space.s2
    },
    large: {
        gap: space.s2 * 2,
        paddingInline: space.s2
    }
};
const SButtonContents = styled("span", {
    shouldForwardProp: filterProps
})(({ isLoading = false, scale = defaultScale })=>{
    const { gap, paddingInline } = contentDimensions[scale];
    return {
        ...contentDimensions[scale],
        alignItems: "center",
        display: "flex",
        gap,
        justifyContent: "center",
        padding: `0 ${paddingInline}px`,
        opacity: isLoading ? 0 : 1,
        minWidth: 0
    };
});
const iconDimensions = {
    small: {
        height: space.s3,
        width: space.s3,
        padding: 1
    },
    medium: {
        height: space.s3 * 1.25,
        width: space.s3 * 1.25,
        padding: 2
    },
    large: {
        height: space.s3 * 1.5,
        width: space.s3 * 1.5,
        padding: 3
    }
};
const SButtonIcon = styled("span", {
    shouldForwardProp: filterProps
})(({ isLoading = false, scale = defaultScale })=>{
    const { height, padding, width } = iconDimensions[scale];
    return {
        alignItems: "center",
        display: "flex",
        flex: "none",
        height,
        justifyContent: "center",
        opacity: isLoading ? 0 : 1,
        padding,
        width,
        "& > svg": {
            height: "100%",
            width: "100%"
        }
    };
});
const SSpinner = styled(Spinner)({
    alignItems: "center",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0
});
const buttonDimensions = {
    small: {
        fontSize: size.s2,
        lineHeight: space.s3,
        minHeight: controlHeight.small,
        minWidth: controlHeight.small
    },
    medium: {
        fontSize: size.s3,
        lineHeight: space.s3 + space.s1,
        minHeight: controlHeight.medium,
        minWidth: controlHeight.medium
    },
    large: {
        fontSize: size.s4,
        lineHeight: space.s3 + space.s2,
        minHeight: controlHeight.large,
        minWidth: controlHeight.large
    }
};
const disabledColors = {
    backgroundColor: palette.gray3,
    color: palette.gray8
};
const buttonColors = {
    neutral: {
        low: {
            backgroundColor: "transparent",
            color: palette.gray14,
            backgroundColorActive: palette.gray5,
            backgroundColorHover: palette.gray4,
            focusBoxShadow: validFocusStyle.boxShadow
        },
        medium: {
            backgroundColor: palette.gray4,
            color: palette.gray14,
            backgroundColorActive: palette.gray6,
            backgroundColorHover: palette.gray5,
            focusBoxShadow: validFocusStyle.boxShadow
        },
        high: {
            backgroundColor: palette.gray14,
            color: palette.gray1,
            backgroundColorActive: palette.gray12,
            backgroundColorHover: palette.gray13,
            focusBoxShadow: validFocusStyle.boxShadow
        }
    },
    danger: {
        low: {
            backgroundColor: "transparent",
            color: palette.red10,
            backgroundColorActive: palette.red5,
            backgroundColorHover: palette.red4,
            focusBoxShadow: invalidFocusStyle.boxShadow
        },
        medium: {
            backgroundColor: palette.red4,
            color: palette.red11,
            backgroundColorActive: palette.red6,
            backgroundColorHover: palette.red5,
            focusBoxShadow: invalidFocusStyle.boxShadow
        },
        high: {
            backgroundColor: palette.red10,
            color: palette.red1,
            backgroundColorActive: palette.red12,
            backgroundColorHover: palette.red11,
            focusBoxShadow: invalidFocusStyle.boxShadow
        }
    },
    primary: {
        low: {
            backgroundColor: "transparent",
            color: palette.green10,
            backgroundColorActive: palette.green5,
            backgroundColorHover: palette.green4,
            focusBoxShadow: validFocusStyle.boxShadow
        },
        medium: {
            backgroundColor: palette.green4,
            color: palette.green11,
            backgroundColorActive: palette.green6,
            backgroundColorHover: palette.green5,
            focusBoxShadow: validFocusStyle.boxShadow
        },
        high: {
            backgroundColor: palette.green10,
            color: palette.green1,
            backgroundColorActive: palette.green12,
            backgroundColorHover: palette.green11,
            focusBoxShadow: validFocusStyle.boxShadow
        }
    }
};
const getButtonStyles = ({ meaning = defaultMeaning, prominence = defaultProminence, scale = defaultScale })=>{
    const { backgroundColor, backgroundColorActive, backgroundColorHover, color, focusBoxShadow } = buttonColors[meaning][prominence];
    const { fontSize, lineHeight, minHeight, minWidth } = buttonDimensions[scale];
    return {
        ...fontWeight("medium"),
        alignItems: "center",
        appearance: "none",
        backgroundColor,
        border: 0,
        borderRadius: scale === "large" ? space.s2 : space.s1_5,
        color,
        cursor: "pointer",
        display: "flex",
        fontSize: `${fontSize}px`,
        justifyContent: "center",
        lineHeight: `${lineHeight}px`,
        margin: 0,
        opacity: 1,
        minHeight: `${minHeight}px`,
        minWidth: `${minWidth}px`,
        outline: "none",
        padding: `${(minHeight - lineHeight) / 2}px`,
        position: "relative",
        textDecoration: "none",
        transition: `background 80ms ease-in-out, ${transition}`,
        // @ts-ignore - Complains that `&:hover` does not exist on CSSProperties but this works fine in Emotion
        "&:hover": {
            backgroundColor: backgroundColorHover,
            textDecoration: "unset"
        },
        "&:focus-visible": {
            boxShadow: focusBoxShadow
        },
        "&:active, &:active:focus-visible": {
            backgroundColor: backgroundColorActive,
            boxShadow: "none"
        },
        "&:disabled": {
            ...disabledColors,
            cursor: "default"
        }
    };
};
const getStyledButtonComponent = (element)=>{
    // @ts-expect-error StyledComponent can't resolve when either "a", "button" and "typeof Box" are options,
    // but resolves fine when it is either Box or string.
    return styled(element, {
        shouldForwardProp: filterProps
    })(getButtonStyles);
};
const SButton = getStyledButtonComponent("button").withComponent(Box);
const SButtonLink = getStyledButtonComponent("a").withComponent(Box);

export { SButton, SButtonContents, SButtonIcon, SButtonLink, SSpinner, disabledColors, getButtonStyles, getStyledButtonComponent };
